import { TestLogic, IDirection, ILandoltContainer, NextLandoltIs } from './test-logic';
import { GammaCorrectionLogic } from './gamma-correction-logic';
import { IMichelsonResultTelemetryData } from '../../result/util/typings';

export class MichelsonTestLogic extends TestLogic {
    private _landoltColor: string;
    private _largestIndex: number;
    private _currentIndex: number;
    private _smallestCorrectLandoltReducedOpacity: ILandoltContainer;
    private _smallestCorrectLandolt: ILandoltContainer;
    private _testMode: 'contrast' | 'visus';
    private _storagePrefix: string = 'michelson_';
    private _gammaCorrectionLogic: GammaCorrectionLogic = GammaCorrectionLogic.getInstance();

    constructor() {
        super();
        this._landoltColor = this._gammaCorrectionLogic.getCorrectedColor(10);
        this._largestIndex = 0;
        this._currentIndex = 0;
        this._smallestCorrectLandoltReducedOpacity = this._landolts[0];
        this._smallestCorrectLandolt = this._landolts[0];
        this._testMode = 'contrast';
    }

    type = (): string => 'MichelsonTestLogic';

    current(): ILandoltContainer {
        const current = this._landolts[this._currentIndex];
        current.color = this._landoltColor;
        return current;
    }

    apply(direction: IDirection): boolean {
        const current = this._landolts[this._currentIndex];
        ++current.seen;
        if (current.direction.direction === direction) {
            ++current.correctGuess;
            // User has guessed the correct ring direction correctly twice in a row.
            if (current.correctGuess === 2) {
                current.identified = true;
                current.identifiedCorrectly = true;
                this._smallestCorrectLandolt = current;
                if (this._testMode === 'contrast') {
                    this._smallestCorrectLandoltReducedOpacity = current;
                    this._storageService.set(this._storagePrefix + 'scLandoltReduced', JSON.stringify(current));
                }

                this._storageService.set(this._storagePrefix + 'scLandolt', JSON.stringify(current));
                return this._getNextLandolt(NextLandoltIs.SMALLER);
            } else {
                // User guessed that one right but only once. Change the ring direction and show the same size again.
                current.direction = TestLogic._getDifferentDirection(current.direction);
                return true;
            }
        } else {
            ++current.wrongGuess;
            // User has guessed the biggest ring wrongly. Change the ring direction and show the same size again.
            if (this._currentIndex === this._largestIndex && current.wrongGuess === 1) {
                current.direction = TestLogic._getDifferentDirection(current.direction);
                return true;
            }

            // User has already guessed the current ring wrongly and now they did it a second time.
            if (current.wrongGuess === 2) {
                current.identified = true;
                current.identifiedWrongly = true;
            }

            // In case of a wrong guess we don‘t show the same ring again but rather go one step bigger
            return this._getNextLandolt(NextLandoltIs.BIGGER);
        }
    }

    resetState(): void {
        this._resetLandolts();
        this._storageService.del([`${this._storagePrefix}scLandolt`, `${this._storagePrefix}scLandoltReduced`]);
        this._globalLogic.removeTestFromFinishedList('michelson-test');
        this._landoltColor = this._gammaCorrectionLogic.getCorrectedColor(10);
        this._largestIndex = 0;
        this._currentIndex = 0;
        this._smallestCorrectLandoltReducedOpacity = this._landolts[0];
        this._smallestCorrectLandolt = this._landolts[0];
        this._testMode = 'contrast';
    }

    get smallestCorrectLandolt(): ILandoltContainer {
        const buf = this._storageService.get(this._storagePrefix + 'scLandolt');
        if (buf) {
            return JSON.parse(buf);
        }
        return this._smallestCorrectLandolt;
    }

    get smallestCorrectLandoltReducedOpacity(): ILandoltContainer {
        const buf = this._storageService.get(this._storagePrefix + 'scLandoltReduced');
        if (buf) {
            return JSON.parse(buf);
        }

        return this._smallestCorrectLandoltReducedOpacity;
    }

    getMode(): 'contrast' | 'visus' {
        return this._testMode;
    }

    getResult(): { lastReducedOpacity: ILandoltContainer, lastFullOpacity: ILandoltContainer } {
        return {
            lastReducedOpacity: this._smallestCorrectLandoltReducedOpacity,
            lastFullOpacity: this._smallestCorrectLandolt
        }
    }

    getTelemetryData(): IMichelsonResultTelemetryData {
        if (!this.finished) {
            return { visus: null, difference: null };
        }
        const lastIdentified = this.smallestCorrectLandolt;
        const lastIdentifiedReduced = this.smallestCorrectLandoltReducedOpacity;
        return {
            visus: +(lastIdentified.visusOneMeter + this._globalLogic.distance / 100).toFixed(4),
            difference: Math.abs(lastIdentified.id - lastIdentifiedReduced.id)
        };
    }

    private _getNextLandolt(nextLandoltIs: NextLandoltIs = NextLandoltIs.SMALLER): boolean {
        if (nextLandoltIs === NextLandoltIs.SMALLER) {
            // User has just identified the smallest ring.
            if (this._currentIndex === this._max - 1) {
                this._markTestAsFinished();
                return false;
            }

            // User has identified the second last ring. Show them the last one.
            if (this._currentIndex === this._max - 2) {
				--this._currentIndex;
				if (!this._landolts[this._currentIndex].identified) {
					return true;
				}
				this._markTestAsFinished();
				return false;
            }

            // Show two sizes smaller
            this._currentIndex += 2;

            if (this._landolts[this._currentIndex].identified) {
                return this._getNextLandolt(NextLandoltIs.SMALLER);
            }
        } else {
            // User could not identify the biggest ring (contrast) or could not identify the biggest ring they
            // identified during the contrast-test.
            if (this._currentIndex === this._largestIndex) {
                // If the user has just finished the contrast test, repeat the test with full opacity.
                if (this._testMode === 'contrast') {
                    this._switchToVisusMode();
                    return true;
                } else {
                    // User just completed the test in visus-mode.
                    this._markTestAsFinished();
                    return false;
                }
            }

            // Show one size bigger
            --this._currentIndex;

            // If the user has been shown that ring before
            if (this._landolts[this._currentIndex].identified) {
                // If the user has identified that ring correctly.
                if (this._landolts[this._currentIndex].identifiedCorrectly) {
                    // If the user is in contrast-mode.
                    if (this._testMode === 'contrast') {
                        // Set that ring as the biggest ring for the visus test.
                        this._largestIndex = this._currentIndex;
                        // Store the last correctly identified ring with reduced opacity.
                        this._smallestCorrectLandoltReducedOpacity = this._landolts[this._currentIndex];
                        // We want to go back to the landolt the user wasn’t able to identify.
                        ++this._currentIndex;
                        this._switchToVisusMode();
                        return true;
                    } else {
                        // User is in visus-mode and got back to their last correctly identified ring.
                        this._markTestAsFinished();
                        return false;
                    }
                }
                return this._getNextLandolt(NextLandoltIs.BIGGER);
            }
        }
        return true;
    }

    private _switchToVisusMode(): void {
        this._testMode = 'visus';
        this._landoltColor = this._gammaCorrectionLogic.getCorrectedColor(85);
        this._resetLandolts();
    }
}
